<template>
  <div data-component-group="invoice-form" data-component="add-new">
    <slot name="beforeForm" />

    <form novalidate @submit.prevent="submit">
      <magic-container>
        <v-text-field
          v-model="id"
          label="ID"
          required
          type="text"
          :readonly="isDisabled"
        />
        <v-autocomplete
          v-model="business"
          :items="businesses"
          :loading="isLoadingBusinesses"
          prepend-inner-icon="contacts"
          label="Business"
          item-title="name"
          item-value="id"
          :return-object="true"
          required
          :readonly="isDisabled"
          v-model:search="businessSearch"
        />
      </magic-container>

      <magic-container>
        <v-text-field
          v-model="invoiceDate"
          label="Invoice date"
          required
          type="text"
          :readonly="isDisabled"
        />
        <v-text-field
          v-model="postingDate"
          label="Posting date"
          required
          type="text"
          :readonly="isDisabled"
        />
      </magic-container>

      <magic-container v-if="!isCreatorProgramPage">
        <v-text-field
          v-model="dueDate"
          label="Due date"
          required
          type="text"
          :readonly="isDisabled"
        />
        <v-text-field
          v-model="contractId"
          label="Contract / Advertiser"
          required
          type="text"
          :readonly="isDisabled"
        />
      </magic-container>

      <v-text-field
        v-model="legalBusinessName"
        label="Legal business name"
        required
        type="text"
        :readonly="isDisabled"
      />

      <v-text-field
        v-model="address"
        label="Address"
        required
        type="text"
        :readonly="isDisabled"
      />

      <v-text-field
        v-model="city"
        label="City"
        required
        type="text"
        :readonly="isDisabled"
      />

      <magic-container>
        <v-text-field
          v-model="zip"
          label="Zip code"
          required
          type="text"
          :readonly="isDisabled"
        />
        <v-text-field
          v-model="country.name"
          label="Country"
          prepend-inner-icon="public"
          type="text"
          :readonly="isDisabled"
        />
      </magic-container>

      <magic-container>
          <div v-if="!isCreatorProgramPage">
            <v-text-field
              v-model="residence"
              label="Residence"
              prepend-inner-icon="public"
              type="text"
              :readonly="isDisabled"
            />
          </div>

          <div v-if="isCreatorProgramPage">
            <v-text-field
              v-model="taxId"
              label="Tax ID"
              prepend-inner-icon="public"
              type="text"
              :readonly="isDisabled"
            />
          </div>

          <v-text-field
            v-model="vatId"
            label="VAT ID"
            required
            type="text"
            :readonly="isDisabled"
          />
      </magic-container>

      <magic-container>
        <v-text-field
          v-model="costCenter"
          prepend-inner-icon="business"
          label="Cost center"
          required
          type="text"
          :readonly="isDisabled"
        />
        <base-date-picker
          :date="servicePeriod"
          :is-disabled="true"
          placeholder="Service period"
        />
      </magic-container>
    </form>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { isStr, isObj, isNum, debounce } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    setup () {
      return {
        v$: useVuelidate()
      }
    },
    validations() {
      return {
        id: {
          required,
        },
        dueDate: {
          required,
        },
        invoiceDate: {
          required,
        },
        postingDate: {
          required,
        },
      };
    },
    props: {
      invoice: {
        type: Object,
        default: undefined,
      },
      cancelTriggerLabel: {
        type: String,
        default: 'Cancel',
      },
      confirmTriggerLabel: {
        type: String,
        default: 'Add invoice',
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onCancel: {
        type: Function,
        default: undefined,
      },
      onSubmit: {
        type: Function,
        required: true,
      },
    },
    data() {
      return {
        id: '',
        dueDate: '',
        invoiceDate: '',
        postingDate: '',
        linkedInvoice: '',
        business: null,
        businessSearch: '',
        businesses: [],
        contractId: '',
        type: '',
        legalBusinessName: '',
        address: '',
        city: '',
        country: '',
        costCenter: '',
        vatId: '',
        taxId: '',
        zip: '',
        residence: '',
        servicePeriod: '',
        createdAt: '',
        status: null,
        isSubmitted: false,
        isLoadingBusinesses: false,
        isRefreshingBusiness: false,
      };
    },
    computed: {
      hasItem() {
        return isObj(this.invoice);
      },
      isCreatorProgramPage() {
        return this.$route.path.includes('creator-program');
      },
    },
    watch: {
      invoice: {
        handler(nextInvoice) {
          this.clear();
          this.onReceiveInvoice(nextInvoice);
        },
        deep: true,
        immediate: true,
      },
      business() {
        this.isRefreshingBusiness = true;
      },
      businessSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingBusiness) {
            this.isRefreshingBusiness = false;
            return false;
          }

          debouncer(() => this.getBusinesses(nextSearch));
        }
      },
    },
    created() {
      this.onReceiveInvoice(this.invoice);
    },
    methods: {
      onReceiveInvoice(nextInvoice) {
        if (isObj(nextInvoice) && isNum(nextInvoice.id)) {
          this.extractInvoiceData(nextInvoice);
        }
      },
      getBusinesses(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItems', query)
          .then(({ items }) => {
            this.businesses = items;
            return items;
          })
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      cancel() {
        this.clear();
        this.onCancel();
      },
      submit() {
        if (this.isLoading || this.isDisabled) return;

        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.addInvoice();
      },
      clear() {
        // this.v$.$reset();
        this.id = '';
        this.dueDate = '';
        this.invoiceDate = '';
        this.postingDate = '';
        this.linkedInvoice = '';
        this.business = null;
        this.contractId = '';
        this.type = '';
        this.legalBusinessName = '';
        this.address = '';
        this.city = '';
        this.zip = '';
        this.costCenter = '';
        this.vatId = '';
        this.taxId = '';
        this.residence = '';
        this.servicePeriod = '';
        this.createdAt = '';
        this.status = null;
        this.isSubmitted = false;
        this.isRefreshingBusiness = false;
      },
      addInvoice() {
        const { id, business } = this;

        const payment = {
          id,
          business: business.id,
        };

        this.isSubmitted = true;
        return this.onSubmit(payment).then((res) => {
          if (!this.hasItem) {
            return this.clear();
          }

          return res;
        });
      },
      canSubmitRequest() {
        const { id, isSubmitted } = this;

        return id !== '' && !isSubmitted;
      },
      extractInvoiceData(invoice) {
        const {
          invoice_number,
          due_date,
          invoice_date,
          posting_date,
          status,
          business,
          title,
          created_at,
          legal_business_name,
          address,
          city,
          zip,
          country,
          residence,
          vat_id,
          tax_number,
          cost_center,
          service_period_start_date,
          service_period_end_date,
        } = invoice;

        this.id = invoice_number;
        this.dueDate = due_date;
        this.invoiceDate = invoice_date;
        this.postingDate = posting_date;
        this.linkedInvoice = '';
        this.legalBusinessName = legal_business_name;
        this.address = address;
        this.contractId = title;
        this.country = country;
        this.city = city;
        this.zip = zip;
        this.vatId = vat_id;
        this.residence = residence;
        this.taxId = tax_number;
        this.costCenter = cost_center.name;
        this.createdAt = created_at;
        this.status = status;
        this.servicePeriod =
          service_period_start_date + ' - ' + service_period_end_date;
        this.business = business;
        this.businesses = [this.business];
        this.isRefreshingBusiness = true;
      },
      onChangeStatus(status) {
        this.status = status;
      },
    },
  };
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='invoice-form'][data-component='add-new'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));

// TODO replace with form-buttons
  .form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .v-btn {
      margin-top: 0;
      margin-bottom: 0;

      &.trigger {
        .v-icon {
          margin: 0 6px 0 0;
        }
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
